import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  CircularProgress,
  Box,
  Paper,
  Divider,
  Link,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import baseURL from "../user/config";
import { toast } from "react-toastify";
import { PrimaryButton, OutlinedButton } from "../../Reusable/Button";

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading"); // "loading" | "success" | "error"
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [email, setEmail] = useState("");
  const [resending, setResending] = useState(false);

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get("token");
      const emailFromQuery = searchParams.get("email");
      if (emailFromQuery) setEmail(emailFromQuery);

      if (!token) {
        setErrorMessage("Verification token is missing or invalid.");
        setStatus("error");
        toast.error("Verification token is missing or invalid.");
        return;
      }

      try {
        const res = await fetch(`${baseURL}/org/verify-email`, { // URL without the token
          method: "POST", // Changed to POST
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }), // Token is now in the request body
        });

        const data = await res.json();

        if (res.ok && data.success) {
          setSuccessMessage(data.message);
          setStatus("success");
          toast.success(data.message);
        } else {
          setErrorMessage(data.message || "Verification failed");
          setStatus("error");
          toast.error(data.message || "Verification failed");
        }
      } catch (err) {
        console.error(err);
        setErrorMessage(err.message || "An unexpected error occurred.");
        setStatus("error");
        toast.error(err.message || "An unexpected error occurred.");
      }
    };

    verifyEmail();
  }, [searchParams]);

  const handleResend = async () => {
    if (!email) {
      toast.error("Email is missing. Cannot resend verification.");
      return;
    }

    setResending(true);
    try {
      const res = await fetch(`${baseURL}/org/resend-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.message || "Failed to resend verification email");
      }

      toast.success(data.message);
    } catch (err) {
      console.error(err);
      toast.error(err.message);
    } finally {
      setResending(false);
    }
  };

  useEffect(() => {
    if (status === "success") {
      const timer = setTimeout(() => navigate("/user/login"), 4000);
      return () => clearTimeout(timer);
    }
  }, [status, navigate]);

  return (
    <Container
      maxWidth="sm"
      sx={{ display: "flex", minHeight: "100vh", alignItems: "center", justifyContent: "center" }}
    >
      <Paper elevation={0} sx={{ p: 4, textAlign: "center", borderRadius: 3 }}>
        {status === "loading" && (
          <>
            <CircularProgress color="primary" />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Verifying your email...
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
              This may take a few seconds. Please do not close the window.
            </Typography>
          </>
        )}
        {status === "success" && (
          <Box>
            <CheckCircleOutlineIcon color="success" sx={{ fontSize: 80 }} />
            <Typography variant="h5" sx={{ mt: 2, fontWeight: "bold" }}>
              {successMessage}
            </Typography>
            <Typography variant="body1" sx={{ mt: 1, mb: 2 }}>
              Your admin account has been activated. You can now log in and start managing your organization.
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <PrimaryButton onClick={() => navigate("/user/login")}>
              Go to Login Now
            </PrimaryButton>
            <Typography variant="caption" display="block" sx={{ mt: 2 }}>
              Redirecting automatically in 4 seconds...
            </Typography>
            <Typography variant="body2" sx={{ mt: 3 }}>
              Need help? Contact{" "}
              <Link href="/contact-us" underline="hover">
                Support
              </Link>
            </Typography>
          </Box>
        )}
        {status === "error" && (
          <Box>
            <ErrorOutlineIcon color="error" sx={{ fontSize: 80 }} />
            <Typography variant="h5" sx={{ mt: 2, fontWeight: "bold", color: "error.main" }}>
              Verification Failed
            </Typography>
            <Typography variant="body1" sx={{ mt: 1, mb: 2 }}>
              {errorMessage || "There was an issue verifying your email."}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 2,
                mt: 2,
                flexWrap: "wrap",
              }}
            >
              <PrimaryButton onClick={handleResend} disabled={resending}>
                {resending ? "Resending..." : "Resend Verification Email"}
              </PrimaryButton>
              <OutlinedButton onClick={() => navigate("/contact-us")}>
                Contact Support
              </OutlinedButton>
            </Box>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default VerifyEmailPage;